import { setAudioModeAsync } from "expo-audio";
import { useEffect, useRef } from "react";
import { Platform } from "react-native";

/**
 * Acquires the iOS audio session (`allowsRecording: true`) while `active`,
 * releases it on every exit path: state change to inactive, error, cancel,
 * unmount, AppState background. Symmetric — there are no parallel cleanup
 * sites so there's no possible "forgot to release in this catch block."
 *
 * On web this is a no-op (no audio session exists). On Android
 * `setAudioModeAsync` is mostly a no-op too but we still call it so that
 * audio focus is requested/released correctly.
 *
 * The token guard prevents a stale cleanup from releasing a fresh session:
 * if `active` flips off → on rapidly, the second acquire creates a new
 * token, and the first effect's cleanup sees `tokenRef.current !== token`
 * and bails out instead of releasing the live session.
 */
export function useAudioSession(active: boolean) {
    const tokenRef = useRef<symbol | null>(null);

    useEffect(() => {
        if (Platform.OS === "web" || !active) {
            return;
        }
        const token = Symbol("audio-session");
        tokenRef.current = token;
        setAudioModeAsync({ allowsRecording: true, playsInSilentMode: true }).catch(() => {});
        return () => {
            if (tokenRef.current !== token) {
                return;
            }
            tokenRef.current = null;
            setAudioModeAsync({ allowsRecording: false, playsInSilentMode: true }).catch(() => {});
        };
    }, [active]);
}
